// @flow

import React, { type Element, useContext, useState } from 'react'
import { getCsrfToken, request } from '../../Common/Request'
import { Checkbox, FormControlLabel, Grid, Typography, Box, Link } from '@material-ui/core'
import { grey } from '@material-ui/core/colors'
import { UserContext } from '../../Common/UserContext'
import AlertSuccess from '../../Common/AlertSuccess'

type Props = {|
  checked: boolean,
  setChecked: (boolean) => void,
  handleServerErrors: ({ [string]: Array<string> }) => void,
|}

function BCPEnrollmentCheckbox({
  checked,
  setChecked,
  handleServerErrors,
}: Props): Element<typeof React.Fragment> {
  const [successMessage, setSuccessMessage] = useState('')

  const userContext = useContext(UserContext)

  async function handleChange(): Promise<boolean> {
    const success = await updateProfile(!checked)
    if (success) {
      setChecked(!checked)
    }
    return true
  }

  function handleSuccess() {
    setSuccessMessage(
      `${
        checked ? 'Disabled' : 'Enabled'
      } Business Cost Premium / Longitudinal Family Physician successfully`
    )
  }

  async function updateProfile(isBCPEnrolled): Promise<boolean> {
    const payload = JSON.stringify({
      is_bcp_enrolled: isBCPEnrolled,
    })
    const path = userContext.updateProfilePath()
    const headers = { 'X-CSRF-Token': getCsrfToken() }

    return await request(path, 'PATCH', headers, payload, handleServerErrors, handleSuccess)
  }

  return (
    <Grid item xs={12}>
      {successMessage && <AlertSuccess message={successMessage} setMessage={setSuccessMessage} />}
      <Box mb={0.5} color={grey[600]}>
        <Typography component="span">
          Business Cost Premium / Longitudinal Family Physician
        </Typography>
      </Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={checked}
            onChange={handleChange}
            color="primary"
            data-testid="bcp_checkbox"
          />
        }
        label={
          <Box color={grey[900]}>
            <Typography component="span" variant="body2">
              I’m enrolled in the Business Cost Premium / Longitudinal Family Physician
            </Typography>
          </Box>
        }
      />
      <Box mb={3} color={grey[600]}>
        <Typography component="span">
          Enrolment in the Business Cost Premium / Longitudinal Family Physician will allow you to
          create a private facility for your office and include it in every claim. If not enrolled,
          you'll only be asked for a publicly listed facility if the claim requires it.{' '}
          <Link
            href="https://support.dr-bill.ca/en/articles/5274972-business-cost-premium-bcp"
            rel="noopener noreferrer"
            target="_blank"
          >
            Learn more about BCP.
          </Link>
          &nbsp;/&nbsp;
          <Link
            href="https://support.dr-bill.ca/en/articles/6949317-longitudinal-family-physician-lfp-payment-model-enrollment-billing-structure"
            rel="noopener noreferrer"
            target="_blank"
          >
            Learn more about LFP.
          </Link>
        </Typography>
      </Box>
    </Grid>
  )
}

export default BCPEnrollmentCheckbox
